import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Button, ButtonGroup, Content, Dialog, DialogContainer, Divider, Flex, Footer, Header, Heading, Image, Link, useDialogContainer, } from '@adobe/react-spectrum';
import { EditorActions } from 'app/stores/editor/actions';
import { useAppDispatch } from 'app/stores/store';
import { Text } from 'app/views/components/typography/Text';
import { useConnect } from 'app/views/hooks/useConnect';
import { analytics } from 'app/utils/analytics';
const sanitize = (str) => str.replaceAll(/[<>\v]/gi, '');
// validate hero image link, allow own images
const validateImageLink = (str) => {
    if (!str) {
        return null;
    }
    if (str.startsWith('https://')) {
        return str;
    }
    if (str.startsWith('annotely://')) {
        return str.replace('annotely://', '/');
    }
    return null;
};
// Parse image copyrights (currently only unsplash is supported)
const formatCopyrights = (copyrights, expectedSource) => {
    switch (expectedSource) {
        case 'undraw': {
            return (React.createElement(React.Fragment, null,
                "Illustration ",
                React.createElement("b", null, copyrights),
                " from",
                ' ',
                React.createElement(Link, { variant: "secondary", isQuiet: true },
                    React.createElement("a", { href: "https://undraw.co/search", target: "_blank", rel: "noreferrer" }, "undraw.co"))));
            break;
        }
        case 'unsplash':
            {
                const regex = /^Photo by \[([\w\s\-_]+)\]\((@[\w]+)\) on (Unsplash)$/i;
                const match = regex.exec(copyrights);
                if (match) {
                    const [, author, handle, source] = match;
                    if (source.toLowerCase() === expectedSource.toLowerCase()) {
                        return (React.createElement(React.Fragment, null,
                            "Photo by",
                            ' ',
                            React.createElement(Link, { variant: "secondary", isQuiet: true },
                                React.createElement("a", { href: `https://unsplash.com/${handle}?utm_source=annotely&utm_medium=referral&utm_content=creditCopyText`, target: "_blank", rel: "noreferrer" }, author)),
                            ' ',
                            "on",
                            ' ',
                            React.createElement(Link, { variant: "secondary", isQuiet: true },
                                React.createElement("a", { href: "https://unsplash.com/?utm_source=annotely&utm_medium=referral&utm_content=creditCopyText", target: "_blank", rel: "noreferrer" }, source))));
                    }
                }
            }
            break;
        default:
            break;
    }
    return React.createElement(React.Fragment, null, sanitize(copyrights));
};
// Parse message to allow center formatting (for earch line) by adding shortened
// css properties at the beginning of the line eq. {tl:c} === textLine:'center' in css
const getMessage = (message) => sanitize(message)
    .split('\n')
    .map(line => {
    var _a;
    if (line.length === 0 || /^[\n\r\s]+$/.test(line)) {
        return { text: line, style: {} };
    }
    const lineRe = /^(\{(?:[a-z]+:[a-z0-9]+,?)+\})?(.*)$/;
    const [, rawStyle, rawText] = (_a = line.match(lineRe)) !== null && _a !== void 0 ? _a : [];
    const style = {};
    if (rawStyle) {
        const styleRe = /[a-z]+:[a-z0-9]+/g;
        rawStyle.replace(styleRe, (match) => {
            const [name, value] = match.split(':');
            switch (name) {
                case 'tl':
                    switch (value) {
                        case 'c':
                            style.textAlign = 'center';
                            break;
                        default:
                            break;
                    }
                    break;
                default:
                    break;
            }
            return '';
        });
    }
    return {
        text: rawText,
        style,
    };
});
const NewsItemDialog = (props) => {
    const { id, hasMore, children } = props;
    const dialog = useDialogContainer();
    const newsItem = useSelector((state) => { var _a; return (_a = state.editor.news) === null || _a === void 0 ? void 0 : _a.find(item => item.id === id); });
    const { dismiss } = dialog;
    const onAction = React.useCallback((value) => {
        analytics({
            action: 'NewsAction',
            label: `Item:${id}:${value}`,
        });
        dismiss();
    }, [id, dismiss]);
    const onDismiss = React.useCallback(() => {
        analytics({
            action: 'NewsDismiss',
            label: `Item:${id}`,
        });
        dismiss();
    }, [id, dismiss]);
    if (!newsItem) {
        return null;
    }
    const { title, message, hero, actions } = newsItem;
    const hasActions = actions && actions.length;
    const [imageUrl, imageCopyrights, imageSource] = Array.isArray(hero)
        ? hero
        : [hero];
    const validatedImageUrl = validateImageLink(imageUrl);
    const dismissLabel = hasMore ? 'Next' : 'Dismiss';
    return (React.createElement(Dialog, null,
        validatedImageUrl && (React.createElement(Image, { slot: "hero", alt: "Cover image for news", src: validatedImageUrl, objectFit: "cover" })),
        title && (React.createElement(React.Fragment, null,
            React.createElement(Heading, null, sanitize(title).replaceAll(/\n/g, ' ')),
            React.createElement(Header, null,
                React.createElement(Text, { size: "XS" }, newsItem.published)),
            React.createElement(Divider, null))),
        React.createElement(Content, null,
            message && (React.createElement(Flex, { direction: "column", gap: "size-50" }, getMessage(message).map(({ text, style }, index) => {
                const key = `${index}_${text.substring(0, 5)}`;
                return (React.createElement(Text, { key: key, size: "S", style: style }, text));
            }))),
            validatedImageUrl && imageCopyrights && (React.createElement(Flex, { direction: "column", marginTop: "size-200", gap: "size-50" },
                React.createElement(Divider, { size: "S" }),
                React.createElement(Text, { size: "XS", color: "gray-400" }, formatCopyrights(imageCopyrights, imageSource))))),
        React.createElement(ButtonGroup, null, hasActions ? (actions.map(action => (React.createElement(Button, { key: action, variant: "cta", onPress: () => onAction(action) }, action)))) : (React.createElement(Button, { variant: "cta", onPress: onDismiss, autoFocus: true }, dismissLabel))),
        children && React.createElement(Footer, null, children)));
};
export const NewsDialog = () => {
    const dispatch = useAppDispatch();
    const [hiding, setHiding] = React.useState(false);
    const { itemId, newsCount } = useConnect(state => {
        var _a, _b;
        const { news } = state.editor;
        return { itemId: (_a = news === null || news === void 0 ? void 0 : news[0]) === null || _a === void 0 ? void 0 : _a.id, newsCount: (_b = news === null || news === void 0 ? void 0 : news.length) !== null && _b !== void 0 ? _b : 0 };
    });
    const onDismiss = React.useCallback(() => {
        if (!hiding && !itemId) {
            return;
        }
        setHiding(true);
        dispatch(EditorActions.dismissNews(itemId));
    }, [itemId, hiding, setHiding, dispatch]);
    useEffect(() => {
        setHiding(false);
    }, [itemId]);
    if (!itemId) {
        return null;
    }
    return (React.createElement(DialogContainer, { isDismissable: false, isKeyboardDismissDisabled: false, onDismiss: onDismiss }, !hiding && (React.createElement(NewsItemDialog, { id: itemId, hasMore: newsCount > 1 }, newsCount > 1 && React.createElement(Text, { size: "XS" },
        "1/",
        newsCount)))));
};
